import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Container,
    Button,
    ColumnLayout,
    Box,
    StatusIndicator,
    KeyValuePairs,
    Table,
    TextFilter,
    Pagination,
    Modal,
} from '@amzn/awsui-components-console';
import React, { useState, useEffect, useContext } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { RegionContext } from '../CustomContext';
import { LoadingSpinner } from '../lib/utils';
import { EmptyState, getFilterCounterText } from '../lab-management/group/utils';
import { useNotifications } from '../NotificationContext';
import { useDrawer } from '../DrawerContext';
import { fetchQueueConfig, fetchQueueMetrics, fetchOverrides, deleteOverride } from './apis';

const OVERRIDE_COLUMN_DEFINITIONS = [
    {
        id: 'botAccount',
        header: 'Bot account',
        cell: (item) => <Box fontWeight="bold">{item.botAccount}</Box>,
        sortingField: 'botAccount',
        minWidth: 200,
        width: 240,
    },
    {
        id: 'deviceType',
        header: 'Device type',
        cell: (item) => item.deviceType,
        sortingField: 'deviceType',
        minWidth: 140,
        width: 160,
    },
    {
        id: 'allocation',
        header: 'Allocation',
        cell: (item) => item.allocation,
        sortingField: 'allocation',
        minWidth: 120,
        width: 140,
    },
    {
        id: 'reason',
        header: 'Reason',
        cell: (item) => item.reason || '-',
        minWidth: 180,
        width: 260,
    },
    {
        id: 'createdBy',
        header: 'Created by',
        cell: (item) => item.createdBy,
        minWidth: 120,
        width: 140,
    },
];

export default function QueueAllocations() {
    const { selectedRegion } = useContext(RegionContext);
    const { addError, addSuccess } = useNotifications();
    const { openDrawer } = useDrawer();
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState(null);
    const [config, setConfig] = useState({});
    const [overrideItems, setOverrideItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
    });

    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(
        overrideItems,
        {
            filtering: {
                noMatch: (
                    <EmptyState
                        title="No matches"
                        subtitle="We can't find a match."
                        action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                    />
                ),
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: { sortingColumn: OVERRIDE_COLUMN_DEFINITIONS[0], isDescending: false },
            },
            selection: {},
        }
    );

    useEffect(() => {
        loadAll();
    }, [selectedRegion]);

    useEffect(() => {
        setSelectedItems([]);
        setDeleteModalVisible(false);
    }, [selectedRegion]);

    const loadAll = async () => {
        setLoading(true);
        try {
            await Promise.all([loadMetrics(), loadConfig(), loadOverrides()]);
        } finally {
            setLoading(false);
        }
    };

    const loadMetrics = async () => {
        setMetricsLoading(true);
        try {
            const resp = await fetchQueueMetrics(selectedRegion.value);
            setMetrics(resp.response || {});
        } catch (e) {
            addError('Failed to load queue metrics. Please try again.');
        }
        setMetricsLoading(false);
    };

    const loadConfig = async () => {
        try {
            const resp = await fetchQueueConfig(selectedRegion.value);
            setConfig(resp.response || {});
        } catch (e) {
            addError('Failed to load queue config. Please try again.');
        }
    };

    const loadOverrides = async () => {
        try {
            const resp = await fetchOverrides(selectedRegion.value);
            setOverrideItems(resp.response || []);
        } catch (e) {
            addError('Failed to load overrides. Please try again.');
        }
    };

    const handleDeleteOverride = async () => {
        if (selectedItems.length === 0) return;
        const item = selectedItems[0];
        setDeleteLoading(true);
        try {
            await deleteOverride(item.botAccount, item.deviceType, selectedRegion.value);
            setSelectedItems([]);
            setDeleteModalVisible(false);
            addSuccess(`Override for ${item.botAccount} (${item.deviceType}) removed successfully.`);
            await loadOverrides();
        } catch (e) {
            addError('Failed to delete override. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleAddOverride = () => {
        const types = (config.valid_device_types || '').split(',').filter(Boolean);
        const options = types.map((t) => ({ label: t, value: t }));
        const prefillBot = selectedItems.length > 0 ? selectedItems[0].botAccount : '';
        openDrawer('create-override', {
            deviceTypeOptions: options,
            prefillBotAccount: prefillBot,
            onSuccess: () => {
                addSuccess('Allocation override created successfully.');
                loadOverrides();
            },
        });
    };

    const formatWaitTime = (seconds) => {
        if (!seconds || seconds === 0) return '0 sec';
        const total = Math.round(seconds);
        const min = Math.floor(total / 60);
        const sec = total % 60;
        if (min === 0) return `${sec} sec`;
        return sec > 0 ? `${min} min ${sec} sec` : `${min} min`;
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <SpaceBetween size="l">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'Admin management', href: '#' },
                    { text: 'Queue and allocations', href: '#/queue-management' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description="Monitor and manage device request queues, device type allocations, team overrides, and timeout settings."
            >
                Queue and allocations
            </Header>

            {/* Queue Metrics */}
            <Container
                header={
                    <Header
                        variant="h2"
                        description="Today's queue activity for the selected region."
                        actions={
                            <Button variant="icon" iconName="refresh" onClick={loadMetrics} loading={metricsLoading} />
                        }
                    >
                        Queue status
                    </Header>
                }
            >
                <ColumnLayout columns={4} variant="text-grid" borders="vertical">
                    <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Enqueued today</Box>
                        <Box variant="awsui-value-large">{metrics?.enqueued_today ?? 0}</Box>
                        <StatusIndicator type="info">Requests queued</StatusIndicator>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Fulfilled today</Box>
                        <Box variant="awsui-value-large">{metrics?.fulfilled_today ?? 0}</Box>
                        <StatusIndicator type="success">Device assigned</StatusIndicator>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Expired today</Box>
                        <Box variant="awsui-value-large">{metrics?.expired_today ?? 0}</Box>
                        <StatusIndicator type={metrics?.expired_today > 0 ? 'error' : 'success'}>
                            {metrics?.expired_today > 0 ? 'Timed out without device' : 'No expirations'}
                        </StatusIndicator>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Avg fulfill wait today</Box>
                        <Box variant="awsui-value-large">{formatWaitTime(metrics?.avg_fulfill_time_seconds)}</Box>
                        <StatusIndicator type="info">Average time to fulfill a request</StatusIndicator>
                    </SpaceBetween>
                </ColumnLayout>
            </Container>

            {/* Settings */}
            <Container
                header={
                    <Header
                        variant="h2"
                        description="Global configuration for queue behavior and allocation defaults."
                        actions={
                            <Button
                                onClick={() =>
                                    openDrawer('edit-settings', {
                                        currentConfig: config,
                                        onSuccess: () => {
                                            addSuccess('Settings updated successfully.');
                                            loadConfig();
                                        },
                                    })
                                }
                            >
                                Edit
                            </Button>
                        }
                    >
                        Settings
                    </Header>
                }
            >
                <KeyValuePairs
                    columns={2}
                    items={[
                        {
                            label: (
                                <SpaceBetween size="xxxs">
                                    <Box fontWeight="bold">Default allocation per device type</Box>
                                    <Box variant="small" color="text-body-secondary">
                                        Maximum devices of the same type a bot can hold before new requests are queued.
                                    </Box>
                                </SpaceBetween>
                            ),
                            value: <StatusIndicator type="success">{config.default_allocation ?? '-'}</StatusIndicator>,
                        },
                        {
                            label: (
                                <SpaceBetween size="xxxs">
                                    <Box fontWeight="bold">Personal account allocation</Box>
                                    <Box variant="small" color="text-body-secondary">
                                        Maximum devices of the same type a personal account can hold at the same time.
                                    </Box>
                                </SpaceBetween>
                            ),
                            value: (
                                <StatusIndicator type="success">{config.personal_account_cap ?? '-'}</StatusIndicator>
                            ),
                        },
                        {
                            label: (
                                <SpaceBetween size="xxxs">
                                    <Box fontWeight="bold">Default queue timeout</Box>
                                    <Box variant="small" color="text-body-secondary">
                                        How long a request waits before expiring when no custom timeout is specified.
                                    </Box>
                                </SpaceBetween>
                            ),
                            value: (
                                <StatusIndicator type="success">
                                    {config.queue_timeout_default ? `${config.queue_timeout_default} minutes` : '-'}
                                </StatusIndicator>
                            ),
                        },
                        {
                            label: (
                                <SpaceBetween size="xxxs">
                                    <Box fontWeight="bold">Maximum queue timeout</Box>
                                    <Box variant="small" color="text-body-secondary">
                                        The longest a caller can request to wait. Values above this are capped.
                                    </Box>
                                </SpaceBetween>
                            ),
                            value: (
                                <StatusIndicator type="success">
                                    {config.queue_timeout_max ? `${config.queue_timeout_max} minutes` : '-'}
                                </StatusIndicator>
                            ),
                        },
                        {
                            label: (
                                <SpaceBetween size="xxxs">
                                    <Box fontWeight="bold">Max pending requests per caller</Box>
                                    <Box variant="small" color="text-body-secondary">
                                        Maximum queued requests per caller. Prevents one caller from flooding the queue.
                                    </Box>
                                </SpaceBetween>
                            ),
                            value: (
                                <StatusIndicator type="success">
                                    {config.max_pending_per_requester ?? '-'}
                                </StatusIndicator>
                            ),
                        },
                    ]}
                />
            </Container>

            {/* Overrides Table */}
            <Table
                {...collectionProps}
                header={
                    <Header
                        variant="h2"
                        description="Per-account allocation overrides. Bot accounts can have custom allocation limits per device type."
                        counter={`(${overrideItems.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadOverrides} iconName="refresh" variant="icon" />
                                <Button
                                    disabled={selectedItems.length === 0}
                                    onClick={() => setDeleteModalVisible(true)}
                                >
                                    Remove
                                </Button>
                                <Button
                                    variant="primary"
                                    iconName="add-plus"
                                    iconAlign="left"
                                    onClick={handleAddOverride}
                                >
                                    Add/Edit override
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        Overrides
                    </Header>
                }
                variant="container"
                resizableColumns={true}
                stickyHeader={true}
                selectionType="single"
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                trackBy={(item) => `${item.botAccount}::${item.deviceType}`}
                items={items}
                columnDefinitions={OVERRIDE_COLUMN_DEFINITIONS}
                pagination={<Pagination {...paginationProps} />}
                filter={
                    <TextFilter
                        {...filterProps}
                        countText={getFilterCounterText(filteredItemsCount)}
                        filteringPlaceholder="Find by bot account or device type"
                    />
                }
                empty={
                    <EmptyState
                        title="No overrides"
                        subtitle="No allocation overrides configured."
                        action={
                            <Button variant="primary" iconName="add-plus" onClick={handleAddOverride}>
                                Add/Edit override
                            </Button>
                        }
                    />
                }
            />
            <Modal
                visible={deleteModalVisible}
                onDismiss={() => setDeleteModalVisible(false)}
                header="Remove allocation override"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setDeleteModalVisible(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleDeleteOverride} loading={deleteLoading}>
                                Remove
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                Are you sure you want to remove this allocation override? The bot account will revert to the default
                allocation.
            </Modal>
        </SpaceBetween>
    );
}
